import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getGuide } from '../content/guides';

/**
 * Lightweight per-route SEO. Sets <title> + description + OG tags on navigation.
 * (On React 19 this would be native <title>/<meta> in the tree; on 18 we manage
 * the document head directly, one small effect, no dependency.)
 */
const BASE = 'Facet, Measured. Not guessed.';
const ORIGIN = 'https://facetlooks.com';
// Absolute image for link unfurls: the real 1200x630 raster social card (shipped
// in public/og.png). Matches the static tags in index.html so client-side route
// changes don't downgrade the unfurl to the favicon.
const OG_IMAGE = `${ORIGIN}/og.png`;

const META: Record<string, { title: string; desc: string }> = {
  '/': { title: BASE, desc: 'The measurement instrument for your face: a real benchmark, a specific plan, and progress you can track. On-device and private.' },
  '/scan': { title: 'Free web scan · Facet', desc: 'Measure 478 points on your face in your browser, private, on-device. See your baseline, percentile, tier, and plan.' },
  '/report': { title: 'Your report · Facet', desc: 'Eight facets, each benchmarked against your cohort: score, percentile, tier, deep per-metric analysis, and your fastest moves.' },
  '/method': { title: 'Method & limits · Facet', desc: 'How Facet measures facial harmony, deterministic geometry, real benchmarks, honest uncertainty. A guide, not a verdict.' },
  '/privacy': { title: 'Privacy-first · Facet', desc: 'Measurement runs on your device. Facet stores derived measurements by default; saving your photo to your account is a separate, explicit opt-in.' },
  '/pricing': { title: 'Pricing · Facet', desc: 'The depth of a $150 report for the price of a coffee a week. Free unlimited re-scans and progress tracking included.' },
  '/guides': { title: 'Guides · Facet', desc: 'Honest, specific method explainers and routines: face shape, skincare, jawline, canthal tilt, no manosphere, no doom.' },
  '/plan': { title: 'Your 30-day plan · Facet', desc: 'A sequenced 30-day plan sorted by leverage: the highest-impact, non-surgical moves for your measured face.' },
  '/progress': { title: 'Progress · Facet', desc: 'Per-facet trend lines, streaks, and a before/after slider. Re-scans are free, forever, so the proof is yours.' },
  '/tools': { title: 'Tools · Facet', desc: 'Preview hairstyles, beard and brows, glasses, and makeup, matched to your measured face shape.' },
  '/account': { title: 'Your account · Facet', desc: 'Your private scan history and data controls, export or delete anytime.' },
  '/download': { title: 'Get Facet', desc: 'Facet for iOS is live; Android is a fast follow. Run the full measurement in your browser today.' },
  '/legal/privacy': { title: 'Privacy Policy · Facet', desc: 'How Facet processes and protects your data, biometric-specific, privacy-first.' },
  '/legal/terms': { title: 'Terms & Conditions · Facet', desc: 'The terms for using Facet.' },
};

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    let entry = META[pathname];
    if (!entry && pathname.startsWith('/guides/')) {
      const guide = getGuide(pathname.split('/guides/')[1] ?? '');
      if (guide) entry = { title: `${guide.title} · Facet`, desc: guide.dek };
    }
    const meta = entry ?? META['/']!;
    const url = `${ORIGIN}${pathname}`;
    document.title = meta.title;
    setMeta('description', meta.desc);
    // Open Graph
    setMeta('og:title', meta.title, 'property');
    setMeta('og:description', meta.desc, 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', OG_IMAGE, 'property');
    // Twitter card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', meta.title);
    setMeta('twitter:description', meta.desc);
    setMeta('twitter:image', OG_IMAGE);
    // canonical
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = url;
  }, [pathname]);
  return null;
}
